import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import AuthLayout from './AuthLayout';
import PasskeyButton from './PasskeyButton';
import { authApi } from '../lib/api';

interface Props {
  onAuth: () => void | Promise<void>;
}

export default function LoginPage({ onAuth }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = params.get('from') || '/';

  // WebAuthn Conditional UI. When the page mounts, we kick off
  // `startAuthentication(opts, true)` — the browser then surfaces any
  // passkeys for this RP in the autofill dropdown of the email field
  // (which has `autocomplete="username webauthn"`). Tap a suggestion
  // and the promise below resolves with an assertion. If the user
  // ignores it and types a password instead, this just sits dormant.
  const conditionalStartedRef = useRef(false);
  useEffect(() => {
    if (conditionalStartedRef.current) return;
    conditionalStartedRef.current = true;

    let cancelled = false;
    (async () => {
      const PKC = (typeof window !== 'undefined' ? window.PublicKeyCredential : undefined) as
        | (typeof window.PublicKeyCredential & {
            isConditionalMediationAvailable?: () => Promise<boolean>;
          })
        | undefined;
      if (!PKC?.isConditionalMediationAvailable) return;
      try {
        const supported = await PKC.isConditionalMediationAvailable();
        if (!supported || cancelled) return;
        const opts = await authApi.passkeyAuthStart();
        if (cancelled) return;
        const response = await startAuthentication(
          opts as Parameters<typeof startAuthentication>[0],
          true,
        );
        if (cancelled) return;
        await authApi.passkeyAuthFinish(response);
        await onAuth();
        navigate(from, { replace: true });
      } catch {
        // Conditional UI was dismissed, aborted, or unsupported. Ignore —
        // the user can still type their password or click the button.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [from, navigate, onAuth]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await authApi.login(email.trim(), password);
      await onAuth();
      navigate(from, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Sign-in failed');
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Penobscot Morning admin portal"
      footer={
        <>
          <Link to="#" onClick={(e) => { e.preventDefault(); const em = prompt('Send a password reset to which email?'); if (em) authApi.passwordResetRequest(em.trim()).then(() => alert("If that account exists, we've sent a reset link.")); }} className="text-blue-700 hover:underline">Forgot password?</Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Email</label>
          <input
            autoFocus
            type="email"
            autoComplete="username webauthn"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Password</label>
          <input
            type="password"
            autoComplete="current-password webauthn"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="input"
          />
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <button type="submit" disabled={loading} className="w-full btn-primary justify-center py-2">
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="my-4 flex items-center gap-2 text-xs text-slate-400">
        <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
      </div>
      <PasskeyButton onAuth={() => { onAuth(); navigate(from, { replace: true }); }} />
    </AuthLayout>
  );
}
